/**
 * Shared action handlers for PACS and Worklist tables
 * Following DRY principle to avoid code duplication
 */

import { message } from 'antd';
import {
  makePostCall,
  makeGetCall,
  hasStudyViewingPermission,
  hasReportingPermission,
  RADIOLOGY_URL,
  getUserDetails,
} from './helper';
import { launchViewerOnDualMonitor } from './MonitorManager';

/**
 * Open DICOM viewer for a study
 */
export const openViewer = async (record: any, userDetails: any): Promise<void> => {
  if (hasStudyViewingPermission(userDetails)) {
    const viewerWindow = await launchViewerOnDualMonitor(record?.ps_study_uid, record);
    if (!viewerWindow) message.warning('Could not open viewer window');
  } else {
    message.info("You do not have permission to view study images");
  }
};

/**
 * Open external radiology desk
 */
export const openRadDesk = (record: any, userDetails: any): void => {
  if (hasStudyViewingPermission(userDetails)) {
    const { pacs_order } = record;
    const { patient, po_site } = pacs_order;
    const { pat_pin } = patient;
    window.open(`${RADIOLOGY_URL(pat_pin, po_site || '')}`, '_blank');
  } else {
    message.info("You do not have the permission to view the study images");
  }
};

/**
 * Open report editor in new tab
 */
export const openReport = async (record: any, userDetails: any): Promise<void> => {
  if (hasReportingPermission(userDetails)) {
    const data = {
      order_ids: [record?.pacs_order?.pacs_ord_id].join(','),
    };
    const params = new URLSearchParams(data).toString();
    window.open(`/report?${params}`, '_blank');
  } else {
    message.info("You do not have the permission to do reporting");
  }
};

/**
 * Open multiple selected studies for reporting
 */
export const openSelectedStudies = (selectedRowKeys: any[], userDetails: any): void => {
  if (hasReportingPermission(userDetails)) {
    const data = {
      order_ids: selectedRowKeys.join(','),
    };
    const params = new URLSearchParams(data).toString();
    window.open(`/report?${params}`, '_blank');
  } else {
    message.info("You do not have the permission to do reporting");
  }
};

/**
 * Print report PDF
 */
export const printReport = async (
  rec: any,
  setPrintLoading: (loading: boolean) => void
): Promise<void> => {
  setPrintLoading(true);
  const { pacs_order } = rec;
  try {
    const res = await makePostCall('/print-acc-report', {
      order_id: pacs_order.pacs_ord_id,
      user_id: getUserDetails()?.username,
    }, {
      responseType: "arraybuffer",
    });

    const pdfBlob = new Blob([res.data], { type: "application/pdf" });
    const pdf_url = URL.createObjectURL(pdfBlob);
    const printWindow = window.open(pdf_url, "_blank");
    printWindow.print();
  } catch (err) {
    console.log("Error", err);
    message.error("Failed to print report");
  } finally {
    setPrintLoading(false);
  }
};

/**
 * View report PDF in modal
 */
export const viewReport = async (
  rec: any,
  setPrintLoading: (loading: boolean) => void,
  setViewerModal: (modal: any) => void
): Promise<void> => {
  setPrintLoading(true);
  const { pacs_order } = rec;
  const { pacs_ord_id, patient } = pacs_order;
  const { pat_pacs_id } = patient;

  try {
    const res = await makePostCall('/print-acc-report', {
      order_id: pacs_order.pacs_ord_id,
      user_id: getUserDetails()?.username,
    }, {
      responseType: "arraybuffer",
    });

    setViewerModal({
      visible: true,
      data: res.data,
      patient_id: pat_pacs_id,
      order_id: pacs_ord_id,
      patientData: rec
    });
  } catch (err) {
    console.log("Error", err);
    message.error("Failed to view report");
    setViewerModal({ visible: false, data: null });
  } finally {
    setPrintLoading(false);
  }
};

/**
 * Download report as DOCX
 */
export const downloadDocxReport = async (
  rec: any,
  setPrintLoading: (loading: boolean) => void
): Promise<void> => {
  setPrintLoading(true);
  const { pacs_order } = rec;

  try {
    const res = await makeGetCall(`/download-pat-report-docx?order_id=${pacs_order.pacs_ord_id}`, {
      responseType: "arraybuffer",
    });

    // Extract filename from Content-Disposition header if available
    let filename = `report-${pacs_order.pacs_ord_id}-${pacs_order.patient.pat_name}.docx`;

    // Create blob and download
    const docxBlob = new Blob([res.data], {
      type: "application/vnd.openxmlformats-officedocument.wordprocessingml.document"
    });
    const url = URL.createObjectURL(docxBlob);

    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);

    // Clean up
    setTimeout(() => {
      URL.revokeObjectURL(url);
    }, 1000);

    message.success('DOCX report downloaded successfully');
  } catch (err) {
    console.log("Error downloading DOCX report", err);
    message.error('Failed to download DOCX report');
  } finally {
    setPrintLoading(false);
  }
};

/**
 * Toggle feature states (emergency, confirmation, reporting block)
 */
export const toggleFeatures = async (
  rec: any,
  idx: number,
  feature: 'emergency' | 'confirmation' | 'reporting',
  current: number,
  pageSize: number,
  orders: any,
  setOrders: (orders: any) => void,
  filters: any,
  filterResults: (filters: any) => void
): Promise<void> => {
  const { pacs_order, order_workflow } = rec;
  const { pacs_ord_id, po_priority, po_his_status } = pacs_order;
  const { ow_reporting_blocked } = order_workflow;

  const shouldConfirm = po_his_status !== 'CONFIRMED';
  const currentlyBlocked = ow_reporting_blocked === true || ow_reporting_blocked === 'Y';
  const shouldBlock = !currentlyBlocked;
  const markEmergency = po_priority !== 'EMERGENCY';

  const payload: any = {
    user_id: getUserDetails()?.username,
    order_id: pacs_ord_id,
  };

  let url = '';

  switch (feature) {
    case 'emergency':
      payload.markEmergency = markEmergency;
      url = '/toggle-emergency';
      break;
    case 'confirmation':
      payload.shouldConfirm = shouldConfirm;
      url = '/toggle-confirmation';
      break;
    case 'reporting':
      payload.shouldBlock = shouldBlock;
      url = '/toggle-reporting';
      break;
    default:
      return;
  }

  try {
    await makePostCall(url, payload);
    message.success("Updated successfully");
    filterResults(filters);
  } catch (e) {
    console.log("Error while toggling feature", e);
    message.error(e.message);
  }
};

/**
 * Assign study to technician (self-assignment)
 */
export const assignToSelfTechnician = async (
  rec: any,
  idx: number,
  current: number,
  pageSize: number,
  orders: any,
  setOrders: (orders: any) => void
): Promise<void> => {
  const { pacs_order } = rec;
  const payload = {
    user_id: getUserDetails()?.username,
    order_id: pacs_order?.pacs_ord_id
  };
  const currentIndx = (current - 1) * pageSize + idx;

  try {
    await makePostCall('/assign-to-tech', payload);
    message.success("Updated successfully");
    const prevOrders = [...(orders?.data || [])];
    prevOrders[currentIndx].order_workflow.ow_assigned_technician = getUserDetails()?.username;
    setOrders({ data: prevOrders, loading: false });
  } catch (e) {
    console.log("Error while assigning", e);
    message.error(e.message);
  }
};

/**
 * Upload RIS notes or files
 */
export const uploadRisNotes = async (
  addFileModal: any,
  setAddFileModal: (modal: any) => void
): Promise<void> => {
  const { data, modalType, fileType, notes, file, isNotes } = addFileModal;
  const { pacs_order } = data;
  const formData = new FormData();

  formData.append('order_ids', pacs_order?.pacs_ord_id);
  formData.append('order_id', pacs_order?.pacs_ord_id);
  formData.append('pin', pacs_order?.patient?.pat_pin);

  let headers = {
    'Content-Type': 'multipart/form-data'
  };

  if (isNotes) {
    formData.append('type', 'notes');
    formData.append('notes', notes);
  } else {
    formData.append('file_type', fileType);
    formData.append('file', file);
  }

  const url = isNotes ? '/upload-notes' : '/upload-file';
  formData.append('user_id', getUserDetails()?.username);

  try {
    await makePostCall(url, formData, { headers });
    setAddFileModal(null);
    message.success("Uploaded successfully");
  } catch (error) {
    message.error('Failed to upload file');
  }
};

/**
 * Assign studies to user (HOD functionality)
 */
export const assignToUser = async (
  selectedRowKeys: any[],
  selectedUsersToAssign: any,
  setAssignModal: (modal: any) => void,
  filters: any,
  filterResults: (filters: any) => void
): Promise<void> => {
  setAssignModal({ visible: false, data: {} });

  try {
    await makePostCall('/assign-to-user', {
      order_ids: selectedRowKeys,
      assigned_to: selectedUsersToAssign
    });
    message.success("Assigned successfully");
    filterResults(filters);
  } catch (e) {
    console.log(e);
    message.error("Failed to assign");
  }
};

/**
 * Close report (remove lock)
 */
export const closeReport = async (data: any): Promise<void> => {
  try {
    await makePostCall("/close-report", { order_id: data?.pacs_order?.pacs_ord_id });
  } catch (e) {
    console.log("Error closing report", e);
  }
};
